import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Save, Printer, ArrowLeft } from 'lucide-react';

// Excel-faithful Job Card layout. Each process layer carries (material, density, width, micron, gsm).
// gsm = density * micron. Composite GSM = sum of process gsms + printing GSM (ink) + lamination GSM.
// Film mtr = (1e6 * kg) / (gsm * width). Estimated Ink = printing_gsm * width * mtr / 1e6.
// Solvent = ink * 1.65 (typical). Adhesive/Hardner = lamination_gsm * width * mtr * ratio% / 1e6.
// All numeric fields stay editable so the user can override any computed value.

const blankProcess = (type) => ({ type, material_name: '', density: 0, width: 0, micron: 0, gsm: 0 });
const DEFAULT_PROCESSES = [
  blankProcess('Printing'),
  blankProcess('Lamination 01'),
  blankProcess('Lamination 02'),
  blankProcess('Lamination 03'),
];

const blankMaterial = (process) => ({ process, material_details: '', kgs_required: 0, consumption: null, difference: null, remarks: '' });
const DEFAULT_MATERIALS = [
  blankMaterial('Printing'),
  blankMaterial('Lamination 1'),
  blankMaterial('Lamination 2'),
  blankMaterial('Lamination 3'),
  blankMaterial('Ink'),
  blankMaterial('Solvent'),
  blankMaterial('Adhesive'),
  blankMaterial('Hardner'),
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const cell = { border: '1px solid #c0c0c0', padding: '4px 6px', fontSize: '12px', verticalAlign: 'middle' };
const label = { ...cell, background: '#f3f4f6', fontWeight: 600 };
const num = (n, d = 2) => (n === null || n === undefined || isNaN(n)) ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
const input = (val, on, opts = {}) => (
  <input type={opts.type || 'number'} step="0.0001" value={val ?? ''}
    onChange={(e) => on(opts.type === 'text' ? e.target.value : (parseFloat(e.target.value) || 0))}
    style={{ width: '100%', border: 'none', background: 'transparent', fontSize: '12px', padding: '2px 4px', outline: 'none', textAlign: opts.type === 'text' ? 'left' : 'right', ...(opts.style || {}) }}
  />
);
const textInput = (val, on, style = {}) => (
  <input type="text" value={val ?? ''} onChange={(e) => on(e.target.value)}
    style={{ width: '100%', border: 'none', background: 'transparent', fontSize: '12px', padding: '2px 4px', outline: 'none', ...style }}
  />
);

const JobCard = () => {
  const { soId, itemIndex } = useParams();
  const idx = parseInt(itemIndex || '0', 10);
  const navigate = useNavigate();
  const [so, setSo] = useState(null);
  const [jc, setJc] = useState(null); // existing job card (if any)
  const [template, setTemplate] = useState(null); // saved customer-product BOM
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    sales_order_id: soId,
    sales_order_item_index: idx,
    sales_order_item_sku: '',
    date: todayISO(),
    job_card_no: '',
    order_quantity: 0,
    customer_id: '', customer_name: '', job_name: '', product_sku: '',
    pouch_length_along: 0, pouch_length_across: 0,
    total_ups_along: 0, total_ups_across: 0,
    print_type: 'Surface', number_of_ply: 1,
    sleeve_size: '', material_price: 0, amount: 0,
    processes: DEFAULT_PROCESSES.map(p => ({ ...p })),
    printing_gsm: 1.5, lamination_gsm: 0, composite_gsm: 0,
    film_kgs_printing: 0, film_mtr_printing: 0,
    film_kgs_lam_1: 0, film_mtr_lam_1: 0,
    film_kgs_lam_2: 0, film_mtr_lam_2: 0,
    film_kgs_lam_3: 0, film_mtr_lam_3: 0,
    estimated_ink_kg: 0, estimated_solvent_kg: 0,
    estimated_adhesive_kg: 0, estimated_hardner_kg: 0,
    ratio_adh: 58.82, ratio_hard: 41.18,
    materials: DEFAULT_MATERIALS.map(m => ({ ...m })),
    total_rm_cost: 0, sales_value: 0,
    approved_by: '', checked_by: '', approval_date: '',
    notes: '',
  });

  // ---------- Loading: SO, existing JC for this line item, template BOM ----------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [soRes, jcRes, nextNoRes] = await Promise.all([
          api.get('/sales/orders').then(r => (r.data || []).find(o => o.id === soId)),
          api.get(`/job-cards/by-sales-order/${soId}/item/${idx}`).catch(() => ({ data: null })),
          api.get('/job-cards/next-no').catch(() => ({ data: { job_card_no: '' } })),
        ]);
        if (!alive) return;
        setSo(soRes);
        const existing = jcRes?.data || jcRes; // single doc for this (so, item)
        setJc(existing);

        const lineItem = soRes?.items?.[idx];
        const jobName = existing?.job_name || lineItem?.name || '';
        const customerId = existing?.customer_id || soRes?.customer_id;

        let tmpl = null;
        if (jobName) {
          try {
            const r = await api.get(`/job-cards/customer-product-bom`, { params: { customer_id: customerId, job_name: jobName } });
            tmpl = r.data;
          } catch { tmpl = null; }
        }
        if (!alive) return;
        setTemplate(tmpl);

        // Build initial form: existing JC > template > defaults; SO fills the identification fields.
        setForm(prev => ({
          ...prev,
          sales_order_id: soId,
          sales_order_item_index: idx,
          sales_order_item_sku: existing?.sales_order_item_sku || lineItem?.sku || '',
          job_card_no: existing?.job_card_no || nextNoRes?.data?.job_card_no || '',
          date: existing?.date || todayISO(),
          customer_id: customerId || '',
          customer_name: existing?.customer_name || soRes?.customer_name || '',
          job_name: jobName,
          product_sku: existing?.product_sku || lineItem?.sku || '',
          order_quantity: existing?.order_quantity || lineItem?.quantity || 0,
          pouch_length_along: existing?.pouch_length_along ?? tmpl?.pouch_length_along ?? 0,
          pouch_length_across: existing?.pouch_length_across ?? tmpl?.pouch_length_across ?? 0,
          total_ups_along: existing?.total_ups_along ?? tmpl?.total_ups_along ?? 0,
          total_ups_across: existing?.total_ups_across ?? tmpl?.total_ups_across ?? 0,
          print_type: existing?.print_type || tmpl?.print_type || 'Surface',
          number_of_ply: existing?.number_of_ply || tmpl?.number_of_ply || 1,
          sleeve_size: existing?.sleeve_size || tmpl?.sleeve_size || '',
          material_price: existing?.material_price || 0,
          amount: existing?.amount || 0,
          processes: (existing?.processes && existing.processes.length)
            ? existing.processes
            : (tmpl?.processes && tmpl.processes.length ? tmpl.processes : DEFAULT_PROCESSES.map(p => ({ ...p }))),
          printing_gsm: existing?.printing_gsm ?? 1.5,
          lamination_gsm: existing?.lamination_gsm ?? 0,
          composite_gsm: existing?.composite_gsm ?? 0,
          film_kgs_printing: existing?.film_kgs_printing ?? 0,
          film_mtr_printing: existing?.film_mtr_printing ?? 0,
          film_kgs_lam_1: existing?.film_kgs_lam_1 ?? 0, film_mtr_lam_1: existing?.film_mtr_lam_1 ?? 0,
          film_kgs_lam_2: existing?.film_kgs_lam_2 ?? 0, film_mtr_lam_2: existing?.film_mtr_lam_2 ?? 0,
          film_kgs_lam_3: existing?.film_kgs_lam_3 ?? 0, film_mtr_lam_3: existing?.film_mtr_lam_3 ?? 0,
          estimated_ink_kg: existing?.estimated_ink_kg ?? 0,
          estimated_solvent_kg: existing?.estimated_solvent_kg ?? 0,
          estimated_adhesive_kg: existing?.estimated_adhesive_kg ?? 0,
          estimated_hardner_kg: existing?.estimated_hardner_kg ?? 0,
          ratio_adh: existing?.ratio_adh ?? tmpl?.ratio_adh ?? 58.82,
          ratio_hard: existing?.ratio_hard ?? tmpl?.ratio_hard ?? 41.18,
          materials: (existing?.materials && existing.materials.length)
            ? existing.materials
            : (tmpl?.materials && tmpl.materials.length ? tmpl.materials : DEFAULT_MATERIALS.map(m => ({ ...m }))),
          total_rm_cost: existing?.total_rm_cost ?? 0,
          sales_value: existing?.sales_value ?? 0,
          approved_by: existing?.approved_by || '',
          checked_by: existing?.checked_by || '',
          approval_date: existing?.approval_date || '',
          notes: existing?.notes || '',
        }));
      } catch (e) { console.error(e); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [soId, idx]);

  // ---------- Helpers ----------
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setProc = (idx, field, v) => setForm(p => {
    const ps = [...p.processes];
    ps[idx] = { ...ps[idx], [field]: v };
    if (field === 'density' || field === 'micron') ps[idx].gsm = (ps[idx].density || 0) * (ps[idx].micron || 0);
    return { ...p, processes: ps };
  });
  const setMat = (idx, field, v) => setForm(p => {
    const ms = [...p.materials];
    ms[idx] = { ...ms[idx], [field]: v };
    if (field === 'consumption' || field === 'kgs_required') {
      const k = parseFloat(ms[idx].kgs_required) || 0;
      const c = parseFloat(ms[idx].consumption);
      ms[idx].difference = isNaN(c) ? null : (c - k);
    }
    return { ...p, materials: ms };
  });

  // Composite GSM = sum(layer.gsm) + printing_gsm (ink) + lamination_gsm (adhesive coat)
  const compositeGsm = useCallback(() => {
    const layers = (form.processes || []).reduce((s, p) => s + (p.gsm || 0), 0);
    return layers + (form.printing_gsm || 0) + (form.lamination_gsm || 0);
  }, [form.processes, form.printing_gsm, form.lamination_gsm]);

  // Excel formulas (verified against the sample):
  //   per-layer  gsm   = density * micron                                   (e.g. 0.91 * 30 = 27.3)
  //   film mtr  (per layer) = 1e6 * film_kg / (layer_gsm * width)            (e.g. 1e6*2025.46/(27.3*920) = 80,644 ✓)
  //   film kg   (per layer) = mtr * layer_gsm * width / 1e6
  //   ink_kg               = printing_gsm * width * film_mtr_printing / 1e6 (e.g. 1.5*920*80644/1e6 = 111.29 ✓)
  //   solvent_kg           = ink_kg * 1.65                                   (industry default, editable below)
  //   adhesive_kg          = lamination_gsm * width_lam * mtr_lam * ratio_adh%/100 / 1e6
  //   hardner_kg           = lamination_gsm * width_lam * mtr_lam * ratio_hard%/100 / 1e6
  const SOLVENT_RATIO = 1.65;
  const recalc = useCallback(() => {
    setForm(p => {
      const composite = (p.processes || []).reduce((s, x) => s + (x.gsm || 0), 0) + (p.printing_gsm || 0) + (p.lamination_gsm || 0);
      const layerGsm = (i) => p.processes[i]?.gsm || 0;
      const layerW = (i) => p.processes[i]?.width || 0;
      const printGsm = layerGsm(0), printW = layerW(0);

      // mtr derived from kg (per-layer formula). If user already entered an mtr we respect it.
      const mtrFromKg = (kg, gsm, w) => (gsm && w) ? (1e6 * kg) / (gsm * w) : 0;
      const film_mtr_printing = p.film_mtr_printing || mtrFromKg(p.film_kgs_printing, printGsm, printW);
      const film_mtr_lam_1   = p.film_mtr_lam_1    || mtrFromKg(p.film_kgs_lam_1,   layerGsm(1), layerW(1));
      const film_mtr_lam_2   = p.film_mtr_lam_2    || mtrFromKg(p.film_kgs_lam_2,   layerGsm(2), layerW(2));
      const film_mtr_lam_3   = p.film_mtr_lam_3    || mtrFromKg(p.film_kgs_lam_3,   layerGsm(3), layerW(3));

      const ink     = (p.printing_gsm || 0) * printW * film_mtr_printing / 1e6;
      const solvent = ink * SOLVENT_RATIO;
      // adhesive/hardner are coated on a lamination layer — use lam 01 area when present
      const lamArea = (layerW(1) || printW) * (film_mtr_lam_1 || film_mtr_printing);
      const adh     = (p.lamination_gsm || 0) * lamArea * ((p.ratio_adh  || 0) / 100) / 1e6;
      const hard    = (p.lamination_gsm || 0) * lamArea * ((p.ratio_hard || 0) / 100) / 1e6;

      const detail = (i) => `${p.processes[i]?.material_name || ''} ${p.processes[i]?.width || ''} MM ${p.processes[i]?.micron || ''} Mic`.trim();
      const materials = p.materials.map(m => {
        switch (m.process) {
          case 'Printing':     return { ...m, kgs_required: p.film_kgs_printing, material_details: m.material_details || detail(0) };
          case 'Lamination 1': return { ...m, kgs_required: p.film_kgs_lam_1,   material_details: m.material_details || detail(1) };
          case 'Lamination 2': return { ...m, kgs_required: p.film_kgs_lam_2,   material_details: m.material_details || detail(2) };
          case 'Lamination 3': return { ...m, kgs_required: p.film_kgs_lam_3,   material_details: m.material_details || detail(3) };
          case 'Ink':          return { ...m, kgs_required: ink };
          case 'Solvent':      return { ...m, kgs_required: solvent };
          case 'Adhesive':     return { ...m, kgs_required: adh };
          case 'Hardner':      return { ...m, kgs_required: hard };
          default:             return m;
        }
      });
      return {
        ...p,
        composite_gsm: composite,
        film_mtr_printing, film_mtr_lam_1, film_mtr_lam_2, film_mtr_lam_3,
        estimated_ink_kg: ink, estimated_solvent_kg: solvent,
        estimated_adhesive_kg: adh, estimated_hardner_kg: hard,
        materials,
      };
    });
  }, []);

  // Live recompute whenever an input that feeds a formula changes
  useEffect(() => {
    if (loading) return;
    recalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.processes, form.printing_gsm, form.lamination_gsm,
    form.film_kgs_printing, form.film_kgs_lam_1, form.film_kgs_lam_2, form.film_kgs_lam_3,
    form.ratio_adh, form.ratio_hard, loading,
  ]);

  // ---------- Save ----------
  const save = async () => {
    if (!form.job_name) { alert('Please enter Job Name (product description)'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      const res = jc?.id
        ? await api.put(`/job-cards/${jc.id}`, payload)
        : await api.post('/job-cards', payload);
      const saved = res?.data || res;
      const bits = [`Job Card #${saved?.job_card_no || ''} saved`];
      if (saved?.bom_id) bits.push(`BOM saved to Production → BOM Master`);
      if (saved?.production_order_number) bits.push(`Production Order ${saved.production_order_number} placed (draft)`);
      bits.push(`Template auto-saved for "${form.customer_name || 'customer'} → ${form.job_name}".`);
      alert(bits.join('\n'));
      navigate(`/sales-orders/job-cards/${soId}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save job card');
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 20 }}>Loading...</div>;
  if (!so) return <div style={{ padding: 20 }}>Sales order not found.<button onClick={() => navigate('/sales-orders')} className="btn btn-secondary" style={{ marginLeft: 12 }}>Back</button></div>;
  const lineItem = so.items?.[idx];

  return (
    <div data-testid="job-card-page" style={{ maxWidth: '1200px', margin: '0 auto', padding: '16px' }}>
      <div className="flex items-center justify-between mb-4" style={{ printColorAdjust: 'exact' }}>
        <button onClick={() => navigate(`/sales-orders/job-cards/${soId}`)} className="btn btn-ghost btn-sm"><ArrowLeft size={14} /> Back to Job Cards</button>
        <div className="flex gap-2">
          {template && <span className="badge badge-info" title="BOM template was loaded for this customer + product">Template loaded</span>}
          {jc?.bom_id && <span className="badge badge-success" title={`BOM ${jc.bom_id}`}>BOM saved</span>}
          {jc?.production_order_number && <span className="badge badge-success" title="Production order auto-placed">PO {jc.production_order_number}</span>}
          <button onClick={recalc} className="btn btn-secondary btn-sm" data-testid="recalc-btn">Recalculate</button>
          <button onClick={() => window.print()} className="btn btn-secondary btn-sm"><Printer size={14} /> Print</button>
          <button onClick={save} disabled={saving} className="btn btn-primary btn-sm" data-testid="save-job-card-btn"><Save size={14} /> {saving ? 'Saving...' : (jc?.id ? 'Update Job Card' : 'Save Job Card')}</button>
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontFamily: 'Arial, sans-serif' }}>
        <tbody>
          {/* Company header */}
          <tr><td colSpan={8} style={{ ...cell, textAlign: 'center', fontWeight: 700, background: '#fde68a', fontSize: '15px' }}>Creative Print &amp; Packaging Ug Ltd</td></tr>

          {/* Row 2: Date | Job Card No | Order Qty */}
          <tr>
            <td style={label}>Date</td>
            <td style={cell}>{textInput(form.date, v => setF('date', v))}</td>
            <td style={label}>Job Card No.</td>
            <td style={cell}>{textInput(form.job_card_no, v => setF('job_card_no', v))}</td>
            <td style={label}>Order Quantity</td>
            <td style={cell} colSpan={3}>{input(form.order_quantity, v => setF('order_quantity', v))}</td>
          </tr>

          {/* Row 3: Customer | Job Name */}
          <tr>
            <td style={label}>Customer Name</td>
            <td style={cell} colSpan={2}>{textInput(form.customer_name, v => setF('customer_name', v))}</td>
            <td style={label}>Job Name</td>
            <td style={cell} colSpan={4}>{textInput(form.job_name, v => setF('job_name', v))}</td>
          </tr>

          {/* Pouch size / Total Ups / Print Type / Ply / Sleeve / Material Price / Amount */}
          <tr>
            <td style={label} colSpan={2}>Pouch size</td>
            <td style={label} colSpan={2}>Total Ups</td>
            <td style={label}>Print Type</td>
            <td style={label}>Number of ply</td>
            <td style={label}>Sleeve Size</td>
            <td style={label}>Material Price / Amount</td>
          </tr>
          <tr>
            <td style={cell}><div style={{ fontSize: 10, color: '#6b7280' }}>Length Along</div>{input(form.pouch_length_along, v => setF('pouch_length_along', v))}</td>
            <td style={cell}><div style={{ fontSize: 10, color: '#6b7280' }}>Length Across</div>{input(form.pouch_length_across, v => setF('pouch_length_across', v))}</td>
            <td style={cell}><div style={{ fontSize: 10, color: '#6b7280' }}>Along</div>{input(form.total_ups_along, v => setF('total_ups_along', v))}</td>
            <td style={cell}><div style={{ fontSize: 10, color: '#6b7280' }}>Across</div>{input(form.total_ups_across, v => setF('total_ups_across', v))}</td>
            <td style={cell}>
              <select value={form.print_type} onChange={(e) => setF('print_type', e.target.value)} style={{ width: '100%', border: 'none', background: 'transparent', fontSize: '12px' }}>
                <option value="Surface">Surface</option><option value="Reverse">Reverse</option>
              </select>
            </td>
            <td style={cell}>{input(form.number_of_ply, v => setF('number_of_ply', v))}</td>
            <td style={cell}>{textInput(form.sleeve_size, v => setF('sleeve_size', v))}</td>
            <td style={cell}>
              <div style={{ display: 'flex', gap: '4px' }}>
                <div style={{ flex: 1 }}><div style={{ fontSize: 10, color: '#6b7280' }}>Price</div>{input(form.material_price, v => setF('material_price', v))}</div>
                <div style={{ flex: 1 }}><div style={{ fontSize: 10, color: '#6b7280' }}>Amount</div>{input(form.amount, v => setF('amount', v))}</div>
              </div>
            </td>
          </tr>

          {/* Process specification table */}
          <tr>
            <td style={label}>Process</td>
            <td style={label}>Type of Process</td>
            <td style={label}>Material Name</td>
            <td style={label}>Density</td>
            <td style={label}>Width</td>
            <td style={label}>Micron</td>
            <td style={label} colSpan={2}>GSM</td>
          </tr>
          {form.processes.map((p, i) => (
            <tr key={i}>
              <td style={cell}>{i === 0 ? 'Print' : `Lam ${i}`}</td>
              <td style={cell}>{p.type}</td>
              <td style={cell}>{textInput(p.material_name, v => setProc(i, 'material_name', v))}</td>
              <td style={cell}>{input(p.density, v => setProc(i, 'density', v))}</td>
              <td style={cell}>{input(p.width, v => setProc(i, 'width', v))}</td>
              <td style={cell}>{input(p.micron, v => setProc(i, 'micron', v))}</td>
              <td style={cell} colSpan={2}>{num(p.gsm, 2)}</td>
            </tr>
          ))}
          <tr>
            <td style={label} colSpan={6}>Printing GSM</td>
            <td style={cell} colSpan={2}>{input(form.printing_gsm, v => setF('printing_gsm', v))}</td>
          </tr>
          <tr>
            <td style={label} colSpan={6}>Lamination GSM</td>
            <td style={cell} colSpan={2}>{input(form.lamination_gsm, v => setF('lamination_gsm', v))}</td>
          </tr>
          <tr>
            <td style={{ ...label, background: '#fde68a' }} colSpan={6}>Composite GSM</td>
            <td style={{ ...cell, fontWeight: 700, background: '#fef3c7' }} colSpan={2}>{num(compositeGsm(), 2)}</td>
          </tr>

          {/* Calculated requirements */}
          <tr>
            <td style={label}>Film kgs Required for Process</td>
            <td style={label}>Kgs</td>
            <td style={label}>Mtr</td>
            <td style={label}>Ink Kgs Required For Print</td>
            <td style={label}>Kgs</td>
            <td style={label}>Lamination materials</td>
            <td style={label} colSpan={2}>Kgs</td>
          </tr>
          <tr>
            <td style={cell}>Printing</td>
            <td style={cell}>{input(form.film_kgs_printing, v => setF('film_kgs_printing', v))}</td>
            <td style={cell}>{input(form.film_mtr_printing, v => setF('film_mtr_printing', v))}</td>
            <td style={cell}>Estimated Ink</td>
            <td style={cell}>{input(form.estimated_ink_kg, v => setF('estimated_ink_kg', v))}</td>
            <td style={cell}>Estimated Adhesive</td>
            <td style={cell} colSpan={2}>{input(form.estimated_adhesive_kg, v => setF('estimated_adhesive_kg', v))}</td>
          </tr>
          <tr>
            <td style={cell}>Lamination 01</td>
            <td style={cell}>{input(form.film_kgs_lam_1, v => setF('film_kgs_lam_1', v))}</td>
            <td style={cell}>{input(form.film_mtr_lam_1, v => setF('film_mtr_lam_1', v))}</td>
            <td style={cell}>Estimated Solvent</td>
            <td style={cell}>{input(form.estimated_solvent_kg, v => setF('estimated_solvent_kg', v))}</td>
            <td style={cell}>Estimated Hardner</td>
            <td style={cell} colSpan={2}>{input(form.estimated_hardner_kg, v => setF('estimated_hardner_kg', v))}</td>
          </tr>
          <tr>
            <td style={cell}>Lamination 02</td>
            <td style={cell}>{input(form.film_kgs_lam_2, v => setF('film_kgs_lam_2', v))}</td>
            <td style={cell}>{input(form.film_mtr_lam_2, v => setF('film_mtr_lam_2', v))}</td>
            <td style={cell}></td><td style={cell}></td>
            <td style={cell}>Ratio of Adh (%)</td>
            <td style={cell} colSpan={2}>{input(form.ratio_adh, v => setF('ratio_adh', v))}</td>
          </tr>
          <tr>
            <td style={cell}>Lamination 03</td>
            <td style={cell}>{input(form.film_kgs_lam_3, v => setF('film_kgs_lam_3', v))}</td>
            <td style={cell}>{input(form.film_mtr_lam_3, v => setF('film_mtr_lam_3', v))}</td>
            <td style={cell}></td><td style={cell}></td>
            <td style={cell}>Ratio of Hard (%)</td>
            <td style={cell} colSpan={2}>{input(form.ratio_hard, v => setF('ratio_hard', v))}</td>
          </tr>

          {/* Material Requirement (BOM that is auto-saved per customer+product) */}
          <tr><td colSpan={8} style={{ ...label, background: '#fde68a', textAlign: 'center' }}>Material Requirement (auto-saved as BOM for {form.customer_name || 'customer'} → {form.job_name || 'product'})</td></tr>
          <tr>
            <td style={label}>Process</td>
            <td style={label} colSpan={2}>Material Details</td>
            <td style={label}>Kgs Required</td>
            <td style={label}>Consumption</td>
            <td style={label}>Difference</td>
            <td style={label} colSpan={2}>Remarks</td>
          </tr>
          {form.materials.map((m, i) => (
            <tr key={i}>
              <td style={cell}>{m.process}</td>
              <td style={cell} colSpan={2}>{textInput(m.material_details, v => setMat(i, 'material_details', v))}</td>
              <td style={cell}>{input(m.kgs_required, v => setMat(i, 'kgs_required', v))}</td>
              <td style={cell}>{input(m.consumption ?? '', v => setMat(i, 'consumption', v))}</td>
              <td style={cell}>{num(m.difference, 2)}</td>
              <td style={cell} colSpan={2}>{textInput(m.remarks, v => setMat(i, 'remarks', v))}</td>
            </tr>
          ))}

          {/* Approvals + totals */}
          <tr>
            <td style={label}>Approved By</td>
            <td style={cell} colSpan={2}>{textInput(form.approved_by, v => setF('approved_by', v))}</td>
            <td style={label}>Total RM Cost</td>
            <td style={cell}>{input(form.total_rm_cost, v => setF('total_rm_cost', v))}</td>
            <td style={label}>Sales Value</td>
            <td style={cell} colSpan={2}>{input(form.sales_value, v => setF('sales_value', v))}</td>
          </tr>
          <tr>
            <td style={label}>Checked By</td>
            <td style={cell} colSpan={2}>{textInput(form.checked_by, v => setF('checked_by', v))}</td>
            <td style={label}>Approval Date</td>
            <td style={cell} colSpan={4}>{textInput(form.approval_date, v => setF('approval_date', v))}</td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
        SO: <strong>{so.order_number}</strong> · Customer: {so.customer_name} · Line item #{idx + 1} of {so.items?.length || 0}: <strong>{lineItem?.name || '—'}</strong>
        {jc?.id ? <span> · Existing Job Card #{jc.job_card_no}</span> : <span> · New job card</span>}
      </div>
    </div>
  );
};

export default JobCard;
